import type {
  OperatorCallsFilters,
  OperatorHotelsFilters,
  OperatorTenantsFilters,
  OperatorUsersFilters,
} from '@hostesa/api-client';
import { requireSession } from '@hostesa/auth-client';
import { Toaster } from '@hostesa/ui';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { getAuthSnapshot } from './lib/auth-bridge';
import { detailPath, ROUTES } from './lib/nav';
import type { RouterContext } from './lib/router-context';
import {
  AuditLogPage,
  CostsPage,
  EventsPage,
  FeatureFlagsPage,
  IncidentsPage,
  ProvidersPage,
  QueuesPage,
} from './routes/diagnostics/diagnostics-pages';
import { LoginPage } from './routes/login/login-page';
import { CallDetailPage } from './routes/oversight/call-detail-page';
import { HotelDetailPage } from './routes/oversight/hotel-detail-page';
import {
  CallsPage,
  HotelsPage,
  parseCallsSearch,
  parseHotelsSearch,
  parseTenantsSearch,
  TenantsPage,
} from './routes/oversight/oversight-pages';
import { TenantDetailPage } from './routes/oversight/tenant-detail-page';
import { UserDetailPage } from './routes/oversight/user-detail-page';
import { parseUsersSearch, UsersPage } from './routes/oversight/users-page';
import { OverviewPage } from './routes/overview/overview-page';
import { RegisterPage } from './routes/register/register-page';
import { NotFoundPage } from './routes/standalone-pages';
import { SupportPage } from './routes/support/support-page';
import { OperatorGuard } from './shell/operator-guard';
import { OperatorRoute } from './shell/operator-route';

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

// ---------------------------------------------------------------------------
// Public routes
// ---------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === 'string' ? (search.next as string) : undefined,
  }),
  component: LoginPage,
});

// ---------------------------------------------------------------------------
// Operator layout (NOT slug-scoped — operator-level)
// ---------------------------------------------------------------------------

const operatorLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'operator',
  // Session gate runs before any data: an absent token redirects to
  // /login?next=. The hostesa_admin operator gate + RBAC happen in
  // <OperatorGuard>/<OperatorRoute> once memberships have loaded (they come from
  // the in-memory auth session, not a route loader).
  beforeLoad: ({ location }) => {
    const { token } = getAuthSnapshot();
    const decision = requireSession(token, location.href);
    // `to` cast: see lib/nav.ts — the backoffice does not own the global router
    // Register augmentation (the platform app does), so paths are cast locally.
    if ('redirectTo' in decision) throw redirect({ to: decision.redirectTo as never });
  },
  component: OperatorGuard,
});

const indexRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: ROUTES.overview as never });
  },
});

const overviewRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'overview',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read">
      <OverviewPage />
    </OperatorRoute>
  ),
});

const registerRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'register',
  component: () => (
    <OperatorRoute permission="admin:provision">
      <RegisterPage />
    </OperatorRoute>
  ),
});

const tenantsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'tenants',
  validateSearch: parseTenantsSearch,
  component: function TenantsRouteComponent() {
    const navigate = useNavigate();
    const search = useSearch({ strict: false }) as OperatorTenantsFilters;
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <TenantsPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({ to: ROUTES.tenants as never, search: next as never })
          }
          onSelectTenant={(id) => void navigate({ to: detailPath.tenantDetail(id) })}
          onRegister={() => void navigate({ to: ROUTES.register as never })}
        />
      </OperatorRoute>
    );
  },
});

const hotelsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'hotels',
  validateSearch: parseHotelsSearch,
  component: function HotelsRouteComponent() {
    const navigate = useNavigate();
    const search = useSearch({ strict: false }) as OperatorHotelsFilters;
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <HotelsPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({ to: ROUTES.hotels as never, search: next as never })
          }
          onSelectHotel={(id) => void navigate({ to: detailPath.hotelDetail(id) })}
        />
      </OperatorRoute>
    );
  },
});

const callsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'calls',
  validateSearch: parseCallsSearch,
  component: function CallsRouteComponent() {
    const navigate = useNavigate();
    const search = useSearch({ strict: false }) as OperatorCallsFilters;
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <CallsPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({ to: ROUTES.calls as never, search: next as never })
          }
          onViewCallDetails={(callId) => void navigate({ to: detailPath.callDetail(callId) })}
        />
      </OperatorRoute>
    );
  },
});

const supportRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'support',
  component: () => (
    <OperatorRoute permission="admin:impersonate">
      <SupportPage />
    </OperatorRoute>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'users',
  validateSearch: parseUsersSearch,
  component: function UsersRouteComponent() {
    const navigate = useNavigate();
    const search = useSearch({ strict: false }) as OperatorUsersFilters;
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <UsersPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({ to: ROUTES.users as never, search: next as never })
          }
          onSelectUser={(userId) => void navigate({ to: detailPath.userDetail(userId) })}
        />
      </OperatorRoute>
    );
  },
});

const auditLogRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'audit-log',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read">
      <AuditLogPage />
    </OperatorRoute>
  ),
});

const featureFlagsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'feature-flags',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read">
      <FeatureFlagsPage />
    </OperatorRoute>
  ),
});

const incidentsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'incidents',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read">
      <IncidentsPage />
    </OperatorRoute>
  ),
});

const queuesRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'queues',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read">
      <QueuesPage />
    </OperatorRoute>
  ),
});

const eventsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'events',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read">
      <EventsPage />
    </OperatorRoute>
  ),
});

const costsRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'costs',
  component: () => (
    <OperatorRoute permission="usage:read">
      <CostsPage />
    </OperatorRoute>
  ),
});

// Provider diagnostics: the ONLY place provider metadata appears. Gated by the
// `backoffice_provider_diagnostics` flag on top of cross-tenant read.
const providersRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'providers',
  component: () => (
    <OperatorRoute permission="admin:cross_tenant_read" flag="backoffice_provider_diagnostics">
      <ProvidersPage />
    </OperatorRoute>
  ),
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'tenants/$id',
  component: function TenantDetailRouteComponent() {
    const navigate = useNavigate();
    const { id } = useParams({ strict: false }) as { id: string };
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <TenantDetailPage
          tenantId={id}
          onSelectHotel={(hotelId) => void navigate({ to: detailPath.hotelDetail(hotelId) })}
          onBack={() => void navigate({ to: ROUTES.tenants as never })}
        />
      </OperatorRoute>
    );
  },
});

const hotelDetailRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'hotels/$id',
  component: function HotelDetailRouteComponent() {
    const navigate = useNavigate();
    const { id } = useParams({ strict: false }) as { id: string };
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <HotelDetailPage
          hotelId={id}
          onBack={() => void navigate({ to: ROUTES.hotels as never })}
        />
      </OperatorRoute>
    );
  },
});

const callDetailRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'calls/$id',
  component: function CallDetailRouteComponent() {
    const navigate = useNavigate();
    const { id } = useParams({ strict: false }) as { id: string };
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <CallDetailPage callId={id} onBack={() => void navigate({ to: ROUTES.calls as never })} />
      </OperatorRoute>
    );
  },
});

const userDetailRoute = createRoute({
  getParentRoute: () => operatorLayoutRoute,
  path: 'users/$id',
  component: function UserDetailRouteComponent() {
    const navigate = useNavigate();
    const { id } = useParams({ strict: false }) as { id: string };
    return (
      <OperatorRoute permission="admin:cross_tenant_read">
        <UserDetailPage userId={id} onBack={() => void navigate({ to: ROUTES.users as never })} />
      </OperatorRoute>
    );
  },
});

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  loginRoute,
  operatorLayoutRoute.addChildren([
    indexRoute,
    overviewRoute,
    registerRoute,
    supportRoute,
    usersRoute,
    userDetailRoute,
    auditLogRoute,
    featureFlagsRoute,
    tenantsRoute,
    tenantDetailRoute,
    hotelsRoute,
    hotelDetailRoute,
    callsRoute,
    callDetailRoute,
    incidentsRoute,
    queuesRoute,
    eventsRoute,
    costsRoute,
    providersRoute,
  ]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

// NOTE: the global `@tanstack/react-router` `Register` augmentation is
// intentionally NOT declared here. The platform app already augments that
// single global interface with its own route tree; declaring a second one would
// collide when both apps are typechecked in one program (repo-root
// `tsc --noEmit`). The apps deploy independently, so the backoffice relies on
// its locally-typed `AppRouter` instead of the global registration.
